import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { TreeNode } from ".";
import { StepItem } from "~/components/build/Show/routes/list/-components/TreeNode/StepItem";
import { CollapseChevron } from "~/components/build/Show/routes/list/-components/TreeNode/CollapseChevron";
import { retryHistoryChildNodes } from "~/components/build/Show/routes/list/-components/TreeNode/retryHistoryNodes";
import {
  CollapsibleState,
  isCollapsibleExpanded,
} from "~/components/build/Show/routes/list/-components/TreeItem/CollapsibleState";
import { STEP_ITEM_HEIGHT } from "~/components/build/Show/routes/list/-components/constants";
import useCurrentJobOrStep from "~/components/build/Show/lib/useCurrentJobOrStep";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { jobRetriesQueryOptions } from "~/components/build/Show/lib/queries/jobRetries";
import { CommandStep, isFailed, isRunning } from "~/lib/pipeline";
import { StepLinkHighlight } from "~/components/build/Show/routes/list/-components/TreeNode/StepLink";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";

interface Props {
  step: CommandStep;
  depth?: number;
  collapsibleState?: CollapsibleState;
  /** Whether this step has past attempts to expand into. */
  hasRetryHistory?: boolean;
}

export function CommandStepItem({
  step,
  depth,
  collapsibleState = CollapsibleState.None,
  hasRetryHistory = false,
}: Props) {
  const { build } = useBuild();
  const { currentStep } = useCurrentJobOrStep();
  const params = useQueryParams();

  const isExpanded = isCollapsibleExpanded(collapsibleState);

  // When retry history is expanded, the selected attempt's own row owns the
  // highlight. Keep this parent row active only while the history is collapsed.
  const selectionIsJobAttempt = Boolean(params.jid);

  const active = useMemo(
    () =>
      (step.uuid === currentStep?.uuid && !(isExpanded && selectionIsJobAttempt)) ||
      (selectionIsJobAttempt && step.statistics?.latestJobId === params.jid && !isExpanded),
    [
      step.uuid,
      step.statistics?.latestJobId,
      currentStep?.uuid,
      params.jid,
      isExpanded,
      selectionIsJobAttempt,
    ],
  );

  // Fetch the retry chain for the latest job when the history is expanded. The
  // query writes the full chain back into the store so the child rows can render.
  useQuery(
    jobRetriesQueryOptions(build, hasRetryHistory ? step.statistics?.latestJobId : null, {
      enabled: hasRetryHistory && isExpanded,
    }),
  );

  let highlight: StepLinkHighlight | undefined;
  if (isFailed(step)) {
    highlight = "failed";
  } else if (isRunning(step)) {
    highlight = "running";
  }

  return (
    <StepItem
      step={step}
      depth={depth}
      active={active}
      highlight={isExpanded ? undefined : highlight}
      arrow={
        hasRetryHistory ? (
          <CollapseChevron
            nodeId={step.uuid}
            collapsibleState={collapsibleState}
            data-testid="retry-history-toggle"
            aria-label={isExpanded ? "Hide retry history" : "Show retry history"}
          />
        ) : undefined
      }
    />
  );
}

export const createCommandStepNode = (step: CommandStep): TreeNode => {
  const hasRetryHistory =
    Features.BuildListJobRetryHistory && (step.statistics?.retriesCount ?? 0) > 0;

  return {
    id: step.uuid,
    height: STEP_ITEM_HEIGHT,
    // Pin the step row while its expanded retry history scrolls beneath it.
    sticky: hasRetryHistory,
    render: ({ depth, collapsibleState }) => (
      <CommandStepItem
        step={step}
        depth={depth}
        collapsibleState={collapsibleState}
        hasRetryHistory={hasRetryHistory}
      />
    ),
    ...(hasRetryHistory
      ? {
          childNodes: (collapsibleState: CollapsibleState) =>
            retryHistoryChildNodes(step, undefined, collapsibleState),
        }
      : {}),
  };
};
